import { WhatsappMessageModel, useRestApiProvider } from "@jugl-web/rest-api";
import {
  apiUTCToLocalDateTime,
  useDebounce,
  usePrevious,
} from "@jugl-web/utils";
import { ChatMessageReplyInfo } from "@web-src/modules/chats/components/CommonMessageBubble/types/ChatMessageReplyInfo";
import { ChatMessageBubbleAction } from "@web-src/modules/chats/components/CommonMessageBubble/types/ChatMessageBubbleAction";
import { FC, useCallback, useEffect, useMemo } from "react";
import { useInView } from "react-intersection-observer";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import isSameDay from "date-fns/isSameDay";
import { WhatsappMessageBubble } from "../WhatsappMessageBubble";
import { useClientSupportPageContext } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { CRMInfoMessage } from "../CRMInfoMessage";

export const WhatsappMessageItem: FC<{
  message: WhatsappMessageModel;
  conversationId: string;
  onShowOnScreen?: (message: WhatsappMessageModel) => void;
  onReplyPreviewClick?: (replyInfo: ChatMessageReplyInfo) => void;
  isHighlighted?: boolean;
  messages: WhatsappMessageModel[];
}> = ({
  message,
  conversationId,
  onShowOnScreen,
  onReplyPreviewClick,
  isHighlighted,
  messages,
}) => {
  const { inView, ref } = useInView();
  const debouncedInView = useDebounce(inView, 100);
  const previousInView = usePrevious(debouncedInView);
  const { setReplyMessage, isChatExpired, isContactBlocked } =
    useClientSupportPageContext();
  const { entityId } = useEntitySelectedProvider();
  const { integrationsApi } = useRestApiProvider();
  const [sendWhatsappMessageReaction] =
    integrationsApi.useSendWhatsappMessageReactionMutation();

  const isMessageActionsDisabled = isChatExpired || isContactBlocked;

  useEffect(() => {
    if (debouncedInView === previousInView) {
      return;
    }
    if (debouncedInView) {
      onShowOnScreen?.(message);
    }
  }, [debouncedInView, onShowOnScreen, message, previousInView]);

  const handleTriggerAction = useCallback(
    (
      action: ChatMessageBubbleAction,
      triggeredMessage: WhatsappMessageModel
    ) => {
      if (action === ChatMessageBubbleAction.REPLY) {
        setReplyMessage(conversationId, triggeredMessage);
      }
    },
    [conversationId, setReplyMessage]
  );

  const handleReaction = useCallback(
    (reaction: string) => {
      if (message.type === "incoming" && message.msg_id) {
        sendWhatsappMessageReaction({
          entityId,
          data: {
            to: conversationId,
            conv_name: message.name || "",
            type: "reaction",
            payload: {
              message_id: message.msg_id,
              emoji: reaction,
            },
          },
        });
      }
    },
    [message, conversationId, entityId, sendWhatsappMessageReaction]
  );

  const groupInfo = useMemo(() => {
    const idx = messages.findIndex((item) => item.msg_id === message.msg_id);

    const result = {
      isFirst: false,
      isLast: false,
    };
    const prevMessage = messages[idx - 1];
    const nextMessage = messages[idx + 1];
    if (
      !prevMessage ||
      prevMessage?.from !== message.from ||
      prevMessage?.type === "info" ||
      prevMessage?.auto_response !== message.auto_response ||
      !isSameDay(
        apiUTCToLocalDateTime(prevMessage.payload.timestamp),
        apiUTCToLocalDateTime(message.payload.timestamp)
      )
    ) {
      result.isFirst = true;
    }
    if (
      !nextMessage ||
      nextMessage?.from !== message.from ||
      nextMessage?.type === "info" ||
      nextMessage.auto_response !== message.auto_response
    ) {
      result.isLast = true;
    }

    return result;
  }, [message, messages]);

  const $content = useMemo(() => {
    if (message.type === "info") {
      return (
        <CRMInfoMessage
          message={message.payload.body}
          isBlocked={message.payload.action === "blocked"}
          timestamp={message.payload.timestamp}
        />
      );
    }

    return (
      <WhatsappMessageBubble
        message={message}
        isHighlighted={isHighlighted}
        onTriggerAction={handleTriggerAction}
        onReaction={handleReaction}
        onReplyPreviewClick={onReplyPreviewClick}
        isMessageActionsDisabled={isMessageActionsDisabled}
        groupInfo={groupInfo}
      />
    );
  }, [
    isMessageActionsDisabled,
    groupInfo,
    message,
    isHighlighted,
    handleTriggerAction,
    handleReaction,
    onReplyPreviewClick,
  ]);

  return <div ref={ref}>{$content}</div>;
};
